// async-mutex-by-key.ts
//
// Hàng đợi FIFO in-process theo KEY. Tách khỏi with-settings-file-lock.ts để cả
// hai file ở dưới ngưỡng 200 dòng (phase-02 R7) — đây là seam tự nhiên: file này
// KHÔNG biết gì về settings.json, chỉ tuần tự hoá theo khoá.
//
// Vì sao cần nó (R9a) — đọc kỹ trước khi "đơn giản hoá":
// Lock FILE chỉ loại trừ giữa các TIẾN TRÌNH. Trong CÙNG một tiến trình, hai lời
// gọi độc lập (vd `Promise.all([writerA(ws), writerB(ws)])`) sẽ cùng thấy lock file
// của nhau và cùng đợi/steal lẫn nhau — kẻ thứ hai chờ tới hết timeout rồi throw,
// dù chẳng có tiến trình nào khác. Nên trong-tiến-trình phải XẾP HÀNG (chờ tới
// lượt), KHÔNG throw.
//
// ⛔ CẤM thay bằng cờ `Set<key>` + throw khi key đã có: `Set` chỉ khoá theo KEY nên
// không phân biệt được "2 lời gọi độc lập chạy song song" với "lời gọi lồng trong
// lời gọi kia" ⇒ mọi Promise.all cùng process sẽ throw ⇒ chính test lost-update của
// #164 đỏ vĩnh viễn. Việc phát hiện LỒNG là của AsyncLocalStorage bên
// with-settings-file-lock.ts, không phải của hàng đợi này.

// Chờ `p` nhưng không quá `deadlineAt`. Trả true = tới lượt, false = hết ngân sách.
// Timer LUÔN được clear: để timer treo thì Node giữ event loop sống thêm, và vitest
// cảnh báo pending timer sau khi test đã xong.
async function waitUntilDeadline(p: Promise<unknown>, deadlineAt: number): Promise<boolean> {
  const remaining = deadlineAt - Date.now();
  if (remaining <= 0) return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<"expired">((resolve) => {
    timer = setTimeout(() => resolve("expired"), remaining);
  });
  try {
    // p không bao giờ reject (xem `mine` bên dưới), nhưng vẫn nuốt phòng xa —
    // một reject lọt ra sẽ thành unhandled rejection làm sập process.
    const winner = await Promise.race([
      p.then(
        () => "turn" as const,
        () => "turn" as const,
      ),
      expired,
    ]);
    return winner === "turn";
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export class AsyncMutexByKey {
  // key → đuôi hàng đợi. Xoá key khi mình là mắt CUỐI để Map không phình theo số
  // workspace từng chạm tới trong một tiến trình dài (vd sync nhiều lần).
  private readonly tails = new Map<string, Promise<void>>();

  /**
   * Xếp hàng cho `key`. Trả hàm release (idempotent) để nhả lượt.
   * Hết ngân sách trước khi tới lượt ⇒ throw `timeoutError()` — và vẫn nhả slot của
   * mình để người xếp sau không bị treo theo.
   */
  async acquire(key: string, deadlineAt: number, timeoutError: () => Error): Promise<() => void> {
    const previous = this.tails.get(key);
    let markDone: () => void = () => {};
    const mine = new Promise<void>((resolve) => {
      markDone = resolve;
    });
    // Đuôi mới = chờ người trước XONG rồi mới tới lượt mình xong.
    const tail = previous
      ? previous.then(
          () => mine,
          () => mine,
        )
      : mine;
    this.tails.set(key, tail);

    let released = false;
    const release = (): void => {
      if (released) return;
      released = true;
      markDone();
      if (this.tails.get(key) === tail) this.tails.delete(key);
    };

    if (previous && !(await waitUntilDeadline(previous, deadlineAt))) {
      release();
      throw timeoutError();
    }
    return release;
  }
}
